interface Sorter {
  value: SortingArg;
  descending?: boolean;
}
interface SorterNormalized {
  value1: any;
  value2: any;
  descending: boolean;
}

type SortingPrimitive = Date | number | string | boolean;
type SortingArg = Sorter | SortingPrimitive;
type SortingFn<T> = (item: T) => SortingArg;

export function orderByDescending<T>(items: T[], func: SortingFn<T>) {
  return orderBy(items, item => ({ value: func(item), descending: true }));
}

export function orderBy<T>(items: T[], ...funcs: Array<SortingFn<T>>) {
  return items.sort((a, b) => {
    let result = 0;
    for (const func of funcs) {
      const { value1, value2, descending } = normalizeSortingArgument(func, a, b);
      result = order(value1, value2, descending);
      if (result !== 0) break;
    }
    return result;
  });
}

function normalizeSortingArgument<T>(func: SortingFn<T>, a: T, b: T): SorterNormalized {
  const first = func(a);
  const second = func(b);
  let descending: boolean = false;
  let value1;
  let value2;
  if (isSortingObject(first) && isSortingObject(second)) {
    descending = first.descending || false;
    value1 = first.value;
    value2 = second.value;
  } else {
    value1 = first;
    value2 = second;
  }
  return { value1, value2, descending };
}

function isSortingObject(arg: SortingArg): arg is Sorter {
  return typeof arg === "object" && arg !== null && !(arg instanceof Date);
}

export function order(first: any, second: any, isDescending = false) {
  if (isDescending) {
    [first, second] = [second, first];
  }

  // Check if both are numbers
  if (!Number.isNaN(+first) && !Number.isNaN(+second)) {
    return first - second;
  }

  // Check if both cannot be evaluated
  if (first === null && second === null) {
    return 0;
  }

  [first, second] = [first, second].map(s => (s || "").toString().toLocaleLowerCase());

  if (first > second) return 1;
  if (first < second) return -1;

  return 0;
}

export function removeFirst<T>(array: T[], func: (item: T) => boolean) {
  return array.splice(array.findIndex(item => func(item)), 1);
}

export function removeAll<T>(array: T[], func: (item: T) => boolean) {
  const indexesToRemove = findIndexes(array, func);
  const removed: T[] = [];
  for (let i = indexesToRemove.length - 1; i >= 0; i--) {
    const indexToRemove = indexesToRemove[i];
    removed.push(...array.splice(indexToRemove, 1));
  }
  return removed;
}

export function findIndexes<T>(array: T[], func: (item: T) => boolean) {
  const initial: number[] = [];
  const indexes = array.reduce((a, e, i) => {
    if (func(e)) {
      a.push(i);
    }
    return a;
  }, initial);
  return indexes;
}

export function splitArray<T>(list: T[], chunkLength: number) {
  const chunks = [];
  let chunk = [];
  let i = 0;
  let l = 0;
  const n = list.length;
  while (i < n) {
    chunk.push(list[i]);
    l++;
    if (l === chunkLength) {
      chunks.push(chunk);
      chunk = [];
      l = 0;
    }
    i++;
  }
  if (chunk.length) {
    chunks.push(chunk);
  }
  return chunks;
}

export function distinct<T>(value: T, index: number, self: T[]) {
  return self.indexOf(value) === index;
}

export function findDuplicates<T>(items: T[]) {
  const seen = new Set<T>();
  const duplicates = new Set<T>();

  for (const item of items) {
    if (seen.has(item)) {
      duplicates.add(item);
    } else {
      seen.add(item);
    }
  }

  return Array.from(duplicates);
}

export function arrayEquals<T>(a: T[], b: T[]) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
}

export function sum<T>(
  source: Iterable<T>,
  selector: (item: T) => number,
): number {
  let total = 0;
  for (const item of source) {
    total += selector(item);
  }
  return total;
}

export function max<T, K>(
  source: Iterable<T>,
  selector: (item: T) => K,
): K | undefined {
  let result: K | undefined;

  for (const item of source) {
    const value = selector(item);

    if (result == null || value > result) {
      result = value;
    }
  }

  return result;
}

export function min<T, K>(
  source: Iterable<T>,
  selector: (item: T) => K,
): K | undefined {
  let result: K | undefined;

  for (const item of source) {
    const value = selector(item);

    if (result == null || value < result) {
      result = value;
    }
  }

  return result;
}

export function maxBy<T, K>(
  source: Iterable<T>,
  selector: (item: T) => K,
): T | undefined {
  let result: T | undefined;
  let maxVal: K | undefined;

  for (const item of source) {
    const value = selector(item);

    if (maxVal == null || value > maxVal) {
      maxVal = value;
      result = item;
    }
  }

  return result;
}

export function minBy<T, K>(
  source: Iterable<T>,
  selector: (item: T) => K,
): T | undefined {
  let result: T | undefined;
  let minVal: K | undefined;

  for (const item of source) {
    const value = selector(item);

    if (minVal == null || value < minVal) {
      minVal = value;
      result = item;
    }
  }

  return result;
}

export function countBy<T>(
  source: Iterable<T>,
  predicate: (item: T) => boolean,
): number {
  let result = 0;

  for (const item of source) {
    if (predicate(item)) {
      result++;
    }
  }

  return result;
}

export function arraysEqualUnordered<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const countMap = new Map<T, number>();

  for (const item of a) {
    countMap.set(item, (countMap.get(item) || 0) + 1);
  }

  for (const item of b) {
    if (!countMap.has(item)) {
      return false;
    }

    const count = countMap.get(item)!;
    if (count === 1) {
      countMap.delete(item);
    } else {
      countMap.set(item, count - 1);
    }
  }

  return countMap.size === 0;
}

export function sortBySequence<TSource, TKey>(
  source: TSource[],
  order: TKey[],
  sourceKeySelector: (item: TSource) => TKey
): TSource[];

export function sortBySequence<TSource, TOrder, TKey>(
  source: TSource[],
  order: TOrder[],
  sourceKeySelector: (item: TSource) => TKey,
  orderKeySelector: (item: TOrder) => TKey
): TSource[];

export function sortBySequence<TSource, TOrder, TKey>(
  source: TSource[],
  order: TOrder[] | TKey[],
  sourceKeySelector: (item: TSource) => TKey,
  orderKeySelector?: (item: TOrder | TKey) => TKey,
): TSource[] {
  // Default `orderKeySelector` to identity function if not provided
  const effectiveOrderKeySelector = orderKeySelector ?? (key => key as TKey);

  // Create a lookup map based on the keys from the source array
  const sourceLookup = new Map<TKey, TSource[]>();
  for (const item of source) {
    const key = sourceKeySelector(item);
    if (!sourceLookup.has(key)) {
      sourceLookup.set(key, []);
    }
    sourceLookup.get(key)!.push(item);
  }

  // Generate the ordered result based on the order array
  const result: TSource[] = [];
  for (const orderItem of order) {
    const key = effectiveOrderKeySelector(orderItem);
    const matches = sourceLookup.get(key);
    if (matches) {
      result.push(...matches);
    }
  }

  return result;
}
